import { ref, nextTick, type Ref } from "vue";
import { useRouter } from "vue-router";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import {
  CreateTransPickupRequestDocument,
  type CreateTransPickupRequestMutation,
  type CreateTransPickupRequestMutationVariables,
  type TransPickupRequestCreateInput,
  type OperationInput,
} from "@/generated/graphql";
import type { FetchResult } from "@apollo/client/core";
import { routerPath } from "@/common/router";
import type { ShopOption } from "../types";

/** 確認・登録・完了フローに必要な依存をまとめた型。 */
interface UseDeliveryMoveRegisterActionsParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  displayConfirmDialog: Ref<boolean>;
  displayCompletedDialog: Ref<boolean>;
  transPickupRequestCreateInput: TransPickupRequestCreateInput;
  shopSelectValues_from: Ref<ShopOption>;
  shopSelectValues_to: Ref<ShopOption>;
  shopData_from_address: Ref<string>;
  shopData_to_address: Ref<string>;
  deliveryAreaName: Ref<string>;
  display_delivery_time: Ref<string>;
  deliveryKbnSelectValues: Ref<{ index: number; item: string }>;
  dummy_payment_amount: Ref<string>;
  deliveryId_0_Flg: Ref<boolean>;
  irregularDeliveryStartTime: Ref<string>;
  irregularDeliveryEndTime: Ref<string>;
  isDoubleDeli: Ref<boolean>;
  isCharterDeli: Ref<boolean>;
  doubleDeliOriconCount: Ref<number | null | undefined>;
  charterDeliOriconCount: Ref<number | null | undefined>;
  paymentRequiredFlg: Ref<boolean>;
  overDeliveryEndTime: Ref<boolean>;
  acceptableEndTime: Ref<string>;
  initialize: () => Promise<void>;
  removeComma: (value: string) => Promise<void>;
  commmaMoney: (value: string) => Promise<void>;
  onClickDeliveryKbn: () => Promise<void>;
}

/** 確認ダイアログ表示・配達依頼登録・完了ダイアログ表示を扱う composable。 */
export const useDeliveryMoveRegisterActions = (params: UseDeliveryMoveRegisterActionsParams) => {
  const {
    errdialog,
    errMessage,
    overlay,
    displayConfirmDialog,
    displayCompletedDialog,
    transPickupRequestCreateInput,
    shopSelectValues_from,
    shopSelectValues_to,
    shopData_from_address,
    shopData_to_address,
    deliveryAreaName,
    display_delivery_time,
    deliveryKbnSelectValues,
    dummy_payment_amount,
    deliveryId_0_Flg,
    irregularDeliveryStartTime,
    irregularDeliveryEndTime,
    isDoubleDeli,
    isCharterDeli,
    doubleDeliOriconCount,
    charterDeliOriconCount,
    paymentRequiredFlg,
    overDeliveryEndTime,
    acceptableEndTime,
    initialize,
    removeComma,
    commmaMoney,
    onClickDeliveryKbn,
  } = params;

  // Vue Router
  const router = useRouter();

  // 配達依頼作成 logInput
  const operationInput: OperationInput = {
    area_code: '10',
    detail: '操作詳細',
    display_category: '画面名',
    operation: '操作',
    user_id: localStorageGetUserId()
  };

  // 確認・完了ダイアログ関連
  const createTransPickupRequestMutation = ref<CreateTransPickupRequestMutation | null | undefined>(null);
  const commaPyamentAmount = ref('');
  const deliveryNotesBefore = ref('');
  const messageDeliveryNotes = ref('');

  // 「登録手続きへ(確認)」ボタン @Click
  async function onClickOpenConfirmDialog(): Promise<void> {
    transPickupRequestCreateInput.ship_from_shop_code = shopSelectValues_from.value.shop_code;
    transPickupRequestCreateInput.delivery_shop_code = shopSelectValues_to.value.shop_code;

    await removeComma(dummy_payment_amount.value);
    if (dummy_payment_amount.value) {
      const regexPattern = /^[0-9]+$/;
      if (!regexPattern.test(dummy_payment_amount.value)) {
        errdialog.value = true;
        errMessage.value = '支払金額は数値およびカンマのみ入力してください';
        return;
      }
    }
    await commmaMoney(dummy_payment_amount.value);

    displayConfirmDialog.value = true;

    const numberFormat = (num: number): string => new Intl.NumberFormat().format(num);
    if ((transPickupRequestCreateInput.payment_amount as number) > 0) {
      commaPyamentAmount.value = numberFormat(transPickupRequestCreateInput.payment_amount!);
    }
    if (transPickupRequestCreateInput.delivery_notes?.includes('"')) {
      deliveryNotesBefore.value = transPickupRequestCreateInput.delivery_notes;
      transPickupRequestCreateInput.delivery_notes = transPickupRequestCreateInput.delivery_notes.replace(/"/g, '');
      messageDeliveryNotes.value = '(※配達備考のダブルクォーテーションは削除されています)';
    }

    isDoubleDeli.value = false;
    isCharterDeli.value = false;
    if (
      doubleDeliOriconCount.value !== null &&
      transPickupRequestCreateInput.unit >= doubleDeliOriconCount.value! &&
      transPickupRequestCreateInput.unit < charterDeliOriconCount.value!
    ) {
      isDoubleDeli.value = true;
    } else if (
      charterDeliOriconCount.value !== null &&
      transPickupRequestCreateInput.unit >= charterDeliOriconCount.value!
    ) {
      isCharterDeli.value = true;
    }
  }

  // キャンセル @Click
  async function onClickConfirmCancel(): Promise<void> {
    if (deliveryNotesBefore.value !== '') {
      transPickupRequestCreateInput.delivery_notes = deliveryNotesBefore.value;
      deliveryNotesBefore.value = '';
      messageDeliveryNotes.value = '';
    }
    displayConfirmDialog.value = false;
  }

  // 在庫移動・回収依頼を登録する @Click
  async function onClickConfirmEntry(): Promise<void> {
    overlay.value = true;
    try {
      await removeComma(dummy_payment_amount.value);
      if (dummy_payment_amount.value) {
        const regexPattern = /^[0-9]+$/;
        if (!regexPattern.test(dummy_payment_amount.value)) {
          errdialog.value = true;
          errMessage.value = '支払金額は数値およびカンマのみ入力してください';
          return;
        }
      }
      await commmaMoney(dummy_payment_amount.value);

      const removed = dummy_payment_amount.value.replace(/,/g, '');
      transPickupRequestCreateInput.payment_amount = parseInt(removed, 10);
      if (!await createDeliveryRequest()) {
        return;
      }

      displayConfirmDialog.value = false;

      nextTick(() => {
        displayCompletedDialog.value = true;
        overlay.value = false;
      });
    } finally {
      overlay.value = false;
    }
  }

  // 在庫移動・回収依頼登録作成
  async function createDeliveryRequest(): Promise<boolean> {
    const nowDate = new Date();
    const strNowDate =
      String(nowDate.getFullYear()) +
      '-' +
      String(nowDate.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(nowDate.getDate()).padStart(2, '0');
    const acceptableEndTimeAry = acceptableEndTime.value.split(':');
    if (
      deliveryId_0_Flg.value &&
      transPickupRequestCreateInput.required_delivery_date === strNowDate &&
      (Number(acceptableEndTimeAry[0]) < nowDate.getHours() ||
        (Number(acceptableEndTimeAry[0]) === nowDate.getHours() &&
          Number(acceptableEndTimeAry[1]) < nowDate.getMinutes()))
    ) {
      overDeliveryEndTime.value = true;
    }

    if (deliveryId_0_Flg.value) {
      transPickupRequestCreateInput.irregular_delivery_start_time = irregularDeliveryStartTime.value + ':00';
      transPickupRequestCreateInput.irregular_delivery_end_time = irregularDeliveryEndTime.value + ':00';
    } else {
      transPickupRequestCreateInput.irregular_delivery_start_time = '';
      transPickupRequestCreateInput.irregular_delivery_end_time = '';
    }

    transPickupRequestCreateInput.double_deli_oricon_flg = isDoubleDeli.value ? 1 : 0;
    transPickupRequestCreateInput.charter_deli_oricon_flg = isCharterDeli.value ? 1 : 0;

    try {
      const apolloClient = await generateApolloClient();
      const result: FetchResult<CreateTransPickupRequestMutation> = await apolloClient.mutate<
        CreateTransPickupRequestMutation,
        CreateTransPickupRequestMutationVariables
      >({
        mutation: CreateTransPickupRequestDocument,
        variables: {
          input: transPickupRequestCreateInput,
          logInput: operationInput
        }
      });
      createTransPickupRequestMutation.value = result.data;

      if (
        !createTransPickupRequestMutation.value ||
        !createTransPickupRequestMutation.value.createTransPickupRequest?.reception_no
      ) {
        errdialog.value = true;
        errMessage.value = '配達依頼の登録に失敗しました。';
        return false;
      }

      let deliveryStartTimeAny: string[];
      let deliveryEndTimeAny: string[];
      if (deliveryId_0_Flg.value) {
        deliveryStartTimeAny = String(createTransPickupRequestMutation.value.createTransPickupRequest?.irregular_delivery_start_time).split(':');
        deliveryEndTimeAny = String(createTransPickupRequestMutation.value.createTransPickupRequest?.irregular_delivery_end_time).split(':');
      } else {
        deliveryStartTimeAny = String(createTransPickupRequestMutation.value.createTransPickupRequest?.delivery_start_time).split(':');
        deliveryEndTimeAny = String(createTransPickupRequestMutation.value.createTransPickupRequest?.delivery_end_time).split(':');
      }

      display_delivery_time.value =
        transPickupRequestCreateInput.required_delivery_date +
        '　' +
        deliveryStartTimeAny[0].padStart(2, '0') +
        ':' +
        deliveryStartTimeAny[1].padStart(2, '0') +
        ' ～ ' +
        deliveryEndTimeAny[0].padStart(2, '0') +
        ':' +
        deliveryEndTimeAny[1].padStart(2, '0');

      return true;
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? '配達依頼の登録に失敗しました。' : '予期せぬエラーが発生しました。';
      return false;
    }
  }

  // 戻る @Click
  async function onClickCompletedGoStart(): Promise<void> {
    displayCompletedDialog.value = false;
    nextTick(async () => {
      await initialize();

      transPickupRequestCreateInput.area_code = '';
      transPickupRequestCreateInput.delivery_id = 1;
      transPickupRequestCreateInput.delivery_notes = '';
      transPickupRequestCreateInput.delivery_shop_code = '';
      transPickupRequestCreateInput.delivery_type = 1;
      transPickupRequestCreateInput.payment_amount = null;
      transPickupRequestCreateInput.payment_method = null;
      transPickupRequestCreateInput.receipt_no = null;
      transPickupRequestCreateInput.register_no = null;
      transPickupRequestCreateInput.required_delivery_date = '';
      transPickupRequestCreateInput.ship_from_shop_code = '';
      transPickupRequestCreateInput.unit = 1;
      transPickupRequestCreateInput.irregular_delivery_start_time = '';
      transPickupRequestCreateInput.irregular_delivery_end_time = '';
      paymentRequiredFlg.value = false;

      // 支払金額をクリア（initialize内のdummy_payment_amountは別参照のため個別にリセット）
      dummy_payment_amount.value = '';
      // 区分を初期値（在庫移動）に戻す
      deliveryKbnSelectValues.value = { index: 1, item: '在庫移動' };
      // 受付可能終了時間超過フラグをリセット
      overDeliveryEndTime.value = false;
      // 備考関連の一時変数をリセット
      deliveryNotesBefore.value = '';
      messageDeliveryNotes.value = '';

      await onClickDeliveryKbn();
      await router.push({ path: routerPath.deliveryMoveRegister });
    });
  }

  // 印刷 @Click
  async function onClickCompletedPrint(): Promise<void> {
    // TODO: implement print flow when backend/API is ready.
  }

  return {
    createTransPickupRequestMutation,
    commaPyamentAmount,
    deliveryNotesBefore,
    messageDeliveryNotes,
    onClickOpenConfirmDialog,
    onClickConfirmCancel,
    onClickConfirmEntry,
    onClickCompletedGoStart,
    onClickCompletedPrint,
  };
};
